import FloorPlan from '../model/FloorPlan';
import RelistAdditionalFieldsToRender from '../model/RelistAdditionalFields';

import propertyUtils from './propertyUtils';

import type { CurrentPropertyProps } from '../model/CurrentProperty';
import type { RelistAdditionalFieldsToRenderProps } from '../model/RelistAdditionalFields';

type AmenitiesValuesList = {
  hasCentralAirConditioning: boolean;
  hasWallAirConditioning: boolean;
  hasWindowAirConditioning: boolean;
  hasForcedAirHeatingSystem: boolean;
  hasBaseboardHeatingSystem: boolean;
  hasHeatPumpHeatingSystem: boolean;
  hasWallHeatingSystem: boolean;
  balconyOrDeck: boolean;
  isFurnished: boolean;
  hasHardwoodFloorCovering: boolean;
  wheelchairAccess: boolean;
  hasAttachedGarageParking: boolean;
  hasDetachedGarageParking: boolean;
  offStreetParking: boolean;
  hasDishWasherAppliance: boolean;
  hasSwimmingPool: boolean;
  hasBicycleStorage: boolean;
  roomIsFurnished?: boolean;
  roomHasPrivateBath?: boolean;
};

const getIsAmenitiesEmpty = (currentProperty: CurrentPropertyProps, isRoomForRent: boolean) => {
  const {
    hasCentralAirConditioning,
    hasWallAirConditioning,
    hasWindowAirConditioning,
    hasForcedAirHeatingSystem,
    hasBaseboardHeatingSystem,
    hasHeatPumpHeatingSystem,
    hasWallHeatingSystem,
    balconyOrDeck,
    isFurnished,
    hasHardwoodFloorCovering,
    wheelchairAccess,
    hasAttachedGarageParking,
    hasDetachedGarageParking,
    offStreetParking,
    hasDishWasherAppliance,
    hasSwimmingPool,
    hasBicycleStorage,
  } = currentProperty;

  const amenitiesValues: AmenitiesValuesList = {
    hasCentralAirConditioning,
    hasWallAirConditioning,
    hasWindowAirConditioning,
    hasForcedAirHeatingSystem,
    hasBaseboardHeatingSystem,
    hasHeatPumpHeatingSystem,
    hasWallHeatingSystem,
    balconyOrDeck,
    isFurnished,
    hasHardwoodFloorCovering,
    wheelchairAccess,
    offStreetParking,
    hasAttachedGarageParking,
    hasDetachedGarageParking,
    hasDishWasherAppliance,
    hasSwimmingPool,
    hasBicycleStorage,
  };

  if (isRoomForRent) {
    const floorplan = currentProperty?.floorplans[0];
    // @ts-expect-error
    amenitiesValues.roomIsFurnished = floorplan?.isFurnished;
    // @ts-expect-error
    amenitiesValues.roomHasPrivateBath = floorplan?.hasPrivateBath;
  }

  const isAmenitiesEmpty = !Object.values(amenitiesValues).some((amenitiesValue) => amenitiesValue);
  const isAdditionalAmenitiesEmtpy = currentProperty?.propertyAmenities?.length === 0;
  return isAmenitiesEmpty && isAdditionalAmenitiesEmtpy;
};

const getRelistingAdditionalFields = (
  currentProperty: CurrentPropertyProps,
  isRoomForRent: boolean,
  // @ts-expect-error toursData
  toursData,
  canAccessSFCostsAndFees: boolean,
): [RelistAdditionalFieldsToRenderProps, boolean] => {
  const { petsAllowed, isApplicationsAccepted, hideAddress, hidePhone, floorplans, photos } = currentProperty;

  const floorPlan = FloorPlan(floorplans[0]);

  const isToursEmpty = toursData?.vrModels?.length + toursData?.externalTours?.length === 0;
  const isPetsAllowedEmpty = propertyUtils.getIsPetsAllowedEmpty(petsAllowed);
  const isAmenitiesEmpty = getIsAmenitiesEmpty(currentProperty, isRoomForRent);
  //  TODO: look into why empty square feet is string but populated square feet is number
  const isSquareFeetEmpty = floorPlan?.squareFeet === '' || Number(floorPlan?.squareFeet) === 0;

  const fieldsToRender: RelistAdditionalFieldsToRenderProps = RelistAdditionalFieldsToRender({
    renderSquareFootageField: isSquareFeetEmpty,
    renderAcceptApplicationsField: !isApplicationsAccepted,
    renderPetsField: isPetsAllowedEmpty,
    renderPhotosField: photos?.length === 0,
    renderToursField: isToursEmpty,
    renderAmenitiesField: isAmenitiesEmpty,
    renderHidePropertyAddressField: hideAddress === true,
    renderAllowContactByPhoneField: hidePhone === true,
    renderCostsAndFeesField: canAccessSFCostsAndFees,
  });

  const renderAdditionalDetailsPage = Object.values(fieldsToRender).some(
    (fieldsToRenderValue) => fieldsToRenderValue === true,
  );

  return [fieldsToRender, renderAdditionalDetailsPage];
};

export { getRelistingAdditionalFields };
